import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik } from 'formik'

import Api from 'services/Api'
import { Alert, List } from 'ui'
import BaseModal from 'components/Modals/BaseModal'
import { Button, Radio, Textarea, Form } from 'components/Forms'
import { closeModal } from 'store/actions/modals'
import { refreshCounter } from 'store/actions/refresh'
import { extractTranslation } from 'services/Utilities'
import { t } from 'translations'

const DailyQuestionnaireModal = ({ userId, lastQuestionnaire }) => {
  const dispatch = useDispatch()
  const { dailyQuestionnaireCustomExplanation, dailyQuestionnaireCustomError, dailyQuestionnaireQuestions = [] } = useSelector(state => state.settings)

  const [errorTriggered, setErrorTriggered] = useState(false)
  const [allGood, setAllGood] = useState(false)
  const [hasError, setHasError] = useState(false)

  let initialQuestionValues = dailyQuestionnaireQuestions.reduce((acc, index) => ({ ...acc, [index]: '' }), {})

  const onSubmit = async ({ explanation, ...values }, actions) => {
    // Check if any of the questions the user answered yes to show the error
    let triggerErrorCalc = false
    for (const [index, question] of dailyQuestionnaireQuestions.entries()) {
      if (!question.triggersErrorMessage) continue
      if (values[index] === 'YES') triggerErrorCalc = true
      if (!values[index]) {
        actions.setSubmitting(false)
        actions.setStatus({ type: 'danger', msg: 'employee.dailyQuestionnaire.QUESTIONNAIRE_MODAL.ANSWER_ALL_QUESTIONS' })
        return
      }
    }

    const { success } = await Api.post('/dailyQuestionnaire/submit', {
      userId,
      answers: dailyQuestionnaireQuestions.map((question, index) => ({
        question: question.text,
        answer: values[index]
      })),
      errorTriggered: triggerErrorCalc,
      explanation
    })
    if (!success) setHasError(true)

    // Tell the user they are not allowed to punch if they answered yes to any question
    if (triggerErrorCalc) setErrorTriggered(true)
    else setAllGood(true)

    dispatch(refreshCounter('dailyQuestionnaire'))
  }

  if (errorTriggered || allGood) {
    const errorAndCustomError = !!errorTriggered && !!dailyQuestionnaireCustomError
    const regularErrorOnly = !!errorTriggered && !dailyQuestionnaireCustomError
    return (
      <BaseModal hideButtons title="employee.dailyQuestionnaire.QUESTIONNAIRE_MODAL">
        <div className="mt-2">
          <Alert variant="danger" show={errorAndCustomError}>
            {extractTranslation(dailyQuestionnaireCustomError, { extractCurrentLanguage: true })}
          </Alert>
          <Alert variant="danger" show={regularErrorOnly}>
            {t('employee.dailyQuestionnaire.QUESTIONNAIRE_MODAL.ERROR_TRIGGERED')}
          </Alert>
          <Alert variant="danger" show={!!hasError}>
            {t('employee.dailyQuestionnaire.QUESTIONNAIRE_MODAL.HAS_ERROR')}
          </Alert>
          <Alert variant="info" show={!!allGood}>
            {t('employee.dailyQuestionnaire.QUESTIONNAIRE_MODAL.THANK_YOU')}
          </Alert>
          <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
            {t(`actions.CLOSE`)}
          </Button>
        </div>
      </BaseModal>
    )
  }

  return (
    <BaseModal hideButtons title="employee.dailyQuestionnaire.QUESTIONNAIRE_MODAL">
      <Formik enableReinitialize={true} initialValues={{ ...initialQuestionValues, explanation: '' }} onSubmit={onSubmit}>
        {({ isSubmitting, status, setStatus }) => {
          return (
            <Form className="mt-2">
              {/* Error message */}
              <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                {status?.msg}
              </Alert>
              <Alert variant="info" className="mb-2" show={!!dailyQuestionnaireCustomExplanation}>
                {extractTranslation(dailyQuestionnaireCustomExplanation, { extractCurrentLanguage: true })}
              </Alert>
              {/* Render the questions */}
              <List>
                {dailyQuestionnaireQuestions.map(({ text }, index) => {
                  return (
                    <List.Item className="text-preline" key={index}>
                      <b>{extractTranslation(text, { extractCurrentLanguage: true })}</b>
                      <div className="mt-2">
                        <Radio name={index} translation="radio" options={['YES', 'NO']} module="components" noLabel buttonRadios />
                      </div>
                    </List.Item>
                  )
                })}
              </List>
              {/* Require explanation if user previously answered yes but is filling questionnaire again */}
              {lastQuestionnaire && lastQuestionnaire.errorTriggered ? (
                <>
                  <Alert variant="warning" className="my-2">
                    {t('employee.dailyQuestionnaire.QUESTIONNAIRE_MODAL.EXPLANATION_WARNING')}
                  </Alert>
                  <Textarea name="explanation" module="employee.dailyQuestionnaire.QUESTIONNAIRE_MODAL" required />
                </>
              ) : null}
              <div className="mt-2">
                <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
                <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                  {t(`actions.CLOSE`)}
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default DailyQuestionnaireModal
